import { useCallback, useEffect, useMemo } from 'react';

import { useInfiniteListConsumerStudentFees } from '@givebacks/api/student';
import { useNavigate } from 'react-router-dom';
import { Center, Loader, LoadingOverlay, Stack, Text } from 'ui';
import { useInViewport } from 'ui/Hooks';

import { StudentPaymentCard } from '../../../molecules/cards/StudentPaymentCard';

interface StudentPaymentsListProps {
  studentId: string;
}

const NoPayments = () => {
  return (
    <Center mih="200px">
      <Stack gap={24}>
        <Stack gap={8}>
          <Text size="18px" fw="600" ta="center">
            No Payments Found
          </Text>
          <Text size="14px" fw="400" ta="center">
            No student payments found.
          </Text>
        </Stack>
      </Stack>
    </Center>
  );
};

const PaidFeesLoader = ({
  isFetchingNextPage,
  handleLoadMoreFees,
}: {
  isFetchingNextPage: boolean;
  handleLoadMoreFees: () => void;
}) => {
  const { ref, inViewport } = useInViewport();

  useEffect(() => {
    if (inViewport && !isFetchingNextPage) {
      handleLoadMoreFees();
    }
  }, [handleLoadMoreFees, inViewport, isFetchingNextPage]);

  return (
    <Center ref={ref} py="xl">
      {isFetchingNextPage && <Loader />}
    </Center>
  );
};

const NoPaymentsGuard = ({ studentId }: { studentId: string }) => {
  const navigate = useNavigate();
  const { data, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteListConsumerStudentFees({
      studentId: studentId,
      queryOptions: {
        searches: {
          status: {
            value: 'payment',
          },
        },
        options: {
          include: 'last_payment_at',
        },
      },
    });
  const handleLoadMoreFees = useCallback(() => {
    if (hasNextPage) {
      void fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  const handleStudentFeeClick = useCallback(
    (feeId: string) => {
      navigate(`fee/${feeId}`);
    },
    [navigate]
  );

  const paymentList = useMemo(() => {
    if (!data || !data.pages?.[0]?.student_fees.length) {
      return null;
    }
    return data.pages.map((page) =>
      page.student_fees.map((fee) => (
        <StudentPaymentCard key={fee.uuid} payment={fee} onClick={handleStudentFeeClick} />
      ))
    );
  }, [data, handleStudentFeeClick]);

  if (!paymentList) {
    return <NoPayments />;
  }

  return (
    <Stack>
      {paymentList}
      <PaidFeesLoader
        isFetchingNextPage={isFetchingNextPage}
        handleLoadMoreFees={handleLoadMoreFees}
      />
    </Stack>
  );
};

const StudentPaymentsList = ({ studentId }: StudentPaymentsListProps) => {
  const { isLoading } = useInfiniteListConsumerStudentFees({
    studentId: studentId,
    queryOptions: {
      searches: {
        status: {
          value: 'payment',
        },
      },
      options: {
        include: 'last_payment_at',
      },
    },
  });

  if (isLoading) {
    return <LoadingOverlay visible />;
  }

  return <NoPaymentsGuard studentId={studentId} />;
};
export { StudentPaymentsList };
